import i18n from "@/i18n";
import toast from "react-hot-toast";
import { isApiClientError } from "@/lib/api";

type DeviceCreateConflictCode =
  | "duplicate_serial_number"
  | "duplicate_software_id";

type DeviceCreateConflictDetails = {
  code: DeviceCreateConflictCode;
  existing_device_display_name?: string;
};

function isDeviceCreateConflictDetails(
  value: Record<string, unknown>,
): value is DeviceCreateConflictDetails {
  return (
    value.code === "duplicate_serial_number" ||
    value.code === "duplicate_software_id"
  );
}

function tError(key: string, params?: Record<string, string>): string {
  return i18n.t(key, { ns: "errors", ...params });
}

const AMENDMENT_REASON_ERROR_KEYS: Record<string, string> = {
  wrong_status: "amendReasonWrongStatus",
  refunded: "amendReasonRefunded",
  refund_retrying: "amendReasonRefundRetrying",
  provider_blocks_amendment: "amendReasonProviderBlocksAmendment",
  missing_visit: "amendReasonMissingVisit",
};

const ACTIVITY_ACTIVATION_MISSING_KEYS: Record<string, string> = {
  description: "activityActivationMissingDescription",
  photos: "activityActivationMissingPhotos",
  menu_items: "activityActivationMissingMenuItems",
  service_items: "activityActivationMissingServiceItems",
  availability: "activityActivationMissingAvailability",
};

function resolveActivityActivationMissing(
  record: Record<string, unknown>,
): string | undefined {
  const missing = record.missing;
  if (!Array.isArray(missing) || missing.length === 0) {
    return undefined;
  }

  const labels = missing
    .filter((key): key is string => typeof key === "string")
    .map((key) => {
      const labelKey = ACTIVITY_ACTIVATION_MISSING_KEYS[key];
      return labelKey ? tError(labelKey) : key;
    })
    .filter((label) => label.length > 0);

  if (labels.length === 0) {
    return undefined;
  }

  return tError("activityActivationIncomplete", { items: labels.join(", ") });
}

function resolveStructuredApiError(
  record: Record<string, unknown>,
): string | undefined {
  if (isDeviceCreateConflictDetails(record)) {
    if (record.code === "duplicate_serial_number") {
      return tError("deviceDuplicateSerialNumber");
    }
    const displayName = record.existing_device_display_name;
    if (typeof displayName === "string" && displayName.trim()) {
      return tError("deviceDuplicateSoftwareId", {
        displayName: displayName.trim(),
      });
    }
    return tError("deviceDuplicateSoftwareIdUnknown");
  }

  if (typeof record.reason === "string") {
    const reasonKey = AMENDMENT_REASON_ERROR_KEYS[record.reason];
    if (reasonKey) {
      return tError(reasonKey);
    }
  }

  const activationMissing = resolveActivityActivationMissing(record);
  if (activationMissing) {
    return activationMissing;
  }

  if (Array.isArray(record.warnings) && record.warnings.length > 0) {
    return tError("amendRequiresOverride");
  }

  return undefined;
}

const TECHNICAL_MARKERS = [
  "[object object]",
  "typeerror",
  "referenceerror",
  "syntaxerror",
  "traceback",
  "stack trace",
  "failed to fetch",
  "network request failed",
  "request failed with status code",
];

function isValidationList(value: unknown): value is unknown[] {
  return Array.isArray(value) && value.length > 0;
}

function readDetailItemMessage(item: unknown): string | undefined {
  if (typeof item === "string" && item.trim()) {
    return item.trim();
  }
  if (!item || typeof item !== "object") {
    return undefined;
  }
  const r = item as Record<string, unknown>;
  if (typeof r.msg === "string" && r.msg.trim()) {
    return r.msg.trim();
  }
  if (typeof r.message === "string" && r.message.trim()) {
    return r.message.trim();
  }
  return undefined;
}

function looksTechnical(message: string): boolean {
  const lower = message.trim().toLowerCase();
  return (
    !lower ||
    lower.length > 240 ||
    TECHNICAL_MARKERS.some((marker) => lower.includes(marker)) ||
    (/^[{[]/.test(message.trim()) &&
      /"detail"|"msg"|"loc"|"code"|"error"/.test(message))
  );
}

export function sanitizeUserFacingErrorText(
  message: string | null | undefined,
  fallback: string,
): string | undefined {
  if (!message) return undefined;
  return looksTechnical(message) ? fallback : message;
}

/**
 * Normalizes API errors to a localized, user-safe message.
 * Unknown or non-localized server details are not surfaced; use `fallbackKey` (errors namespace).
 */
export function getApiErrorMessage(
  error: unknown,
  fallbackKey = "generic",
  localizedFallback?: string,
): string {
  const fb = () => localizedFallback ?? tError(fallbackKey);

  if (isApiClientError(error)) {
    // Only real transport failures are "network" — never label HTTP 4xx/5xx as network.
    if (!error.response) {
      return tError("network");
    }

    const status = error.response.status;
    const data = error.response.data as Record<string, unknown> | undefined;
    const rawDetail = data?.detail ?? data?.details;

    if (isValidationList(rawDetail)) {
      const parts = rawDetail
        .map((item) => readDetailItemMessage(item))
        .filter((m): m is string => Boolean(m));
      if (parts.length > 0) {
        const joined = parts.slice(0, 3).join(" ");
        const msg = sanitizeUserFacingErrorText(joined, fb());
        return msg ?? fb();
      }
      return tError("validation");
    }

    if (typeof rawDetail === "string") {
      const msg = sanitizeUserFacingErrorText(rawDetail, fb());
      return msg ?? fb();
    }

    if (
      rawDetail &&
      typeof rawDetail === "object" &&
      !Array.isArray(rawDetail)
    ) {
      const record = rawDetail as Record<string, unknown>;
      const structured = resolveStructuredApiError(record);
      if (structured) {
        return structured;
      }
      if (typeof record.message === "string") {
        const msg = sanitizeUserFacingErrorText(record.message, fb());
        return msg ?? fb();
      }
    }

    if (typeof data?.message === "string") {
      const msg = sanitizeUserFacingErrorText(data.message, fb());
      return msg ?? fb();
    }

    if (status >= 500) {
      return tError("serverError");
    }

    return fb();
  }

  if (error instanceof Error) {
    return fb();
  }

  return fb();
}

/** Toast a sanitized API error. Pass `localizedFallback` to keep a domain-specific message when the API returns nothing usable. */
export function toastApiError(
  error: unknown,
  fallbackKey = "generic",
  localizedFallback?: string,
): void {
  toast.error(getApiErrorMessage(error, fallbackKey, localizedFallback));
}
